// Libraries
import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

// Layout

// MD2 PRO
import MDBox from 'components/MDBox';

// @mui
import Tooltip from '@mui/material/Tooltip';

// Project Pages And Components

// API
import { getUsers } from 'services/api/user';
import { getProduction } from 'services/api/production';

import AddResponsible from './AddResponsible';

// Material Dashboard 2 PRO React components
import MDAvatar from 'components/MDAvatar';
import AaroLoadingScreen from 'components/Loading';

// Image
import team4 from 'assets/images/team-4.jpg';

/**
 * Fetches production data by ID
 * @param {string} id - Production ID
 * @returns {Promise} Production data
 */
const fetchProduction = async (id) => {
    try {
        const response = await getProduction(id);
        return response.data.result;
    } catch (err) {
        return err;
    }
};

/**
 * Fetches all users
 * @returns {Promise} List of users
 */
const fetchUsers = async () => {
    try {
        const response = await getUsers();
        return response.data.result;
    } catch (err) {
        return err;
    }
};

/**
 * Default avatar styles
 */
const defaultAvatarStyles = {
    border: ({ borders: { borderWidth }, palette: { white } }) => `${borderWidth[2]} solid ${white.main}`,
    cursor: 'pointer',
    position: 'relative',
    '&:hover, &:focus': {
        zIndex: '10',
        transform: 'scale(1.05)',
    },
};

/**
 * Component that displays responsible users for a production
 */
const ProductionResponsibles = ({ showAddButton = true, avatarSize = 'sm', additionalStyle = null, production }) => {
    // State for manual forcing of rerenders
    const [refreshCounter, setRefreshCounter] = useState(0);

    // Get the production ID from the production object
    const productionID = production?._id || null;
    const aaroOrder = production?.aaroOrder || null;

    // State to store latest production data
    const [currentProductionData, setCurrentProductionData] = useState(production);

    // Fetch production data using aaroOrder
    const {
        data: productionData,
        isLoading: isProductionLoading,
        error: productionError,
        refetch: refetchProduction,
    } = useQuery({
        queryKey: ['fetchProduction', aaroOrder],
        queryFn: () => fetchProduction(aaroOrder),
        staleTime: 600000,
        enabled: !!aaroOrder,
    });

    // Update currentProductionData when productionData changes
    useEffect(() => {
        if (productionData) {
            setCurrentProductionData(productionData);
            console.log('Updated production data:', productionData);
        }
    }, [productionData]);

    // Fetch all users
    const {
        data: allUsers,
        isLoading: isUsersLoading,
        error: usersError,
        refetch: refetchUsers,
    } = useQuery({
        queryKey: ['fetchUsers'],
        queryFn: fetchUsers,
        staleTime: 600000,
    });

    // Handler for when update is successful
    const handleUpdateSuccess = () => {
        console.log('Update success callback triggered');

        // Manually refetch the data
        refetchProduction().then((response) => {
            console.log('Refetched production:', response.data);
            if (response.data) {
                setCurrentProductionData(response.data);
            }
            // Force a re-render by incrementing counter
            setRefreshCounter((prev) => prev + 1);
        });
    };

    // Process responsibles data
    const uniqueResponsibles = useMemo(() => {
        console.log('Recalculating uniqueResponsibles with counter:', refreshCounter);

        // If no production ID, return empty array
        if (!productionID) return [];

        // Use the latest production data
        const responsibles = currentProductionData?.responsibles || [];
        console.log('Current responsibles:', responsibles);

        // Ensure responsibles is an array
        if (!Array.isArray(responsibles)) return [];

        // Create unique list of responsibles with user data
        return responsibles.reduce((acc, current) => {
            const currentItem =
                typeof current === 'string' ? { user: allUsers?.find((user) => user._id === current) } : current;

            if (currentItem?.user && !acc.find((item) => item?.user?._id === currentItem.user._id)) {
                acc.push(currentItem);
            }
            return acc;
        }, []);
    }, [productionID, currentProductionData, allUsers, refreshCounter]);

    // Handle loading and error states
    if (!productionID) return null;
    if (isProductionLoading && !currentProductionData) return <AaroLoadingScreen />;
    if (productionError) return `Bir Hata Oluştu: ${productionError.message}`;
    if (isUsersLoading) return <AaroLoadingScreen />;
    if (usersError) return `Bir Hata Oluştu: ${usersError.message}`;

    const avatarStyles = additionalStyle || defaultAvatarStyles;

    return (
        <MDBox display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" flexWrap="wrap">
            {uniqueResponsibles
                .filter((el) => el?.user && typeof el.user === 'object')
                .map((el, index) => (
                    <Tooltip
                        key={`${el.user._id}-${refreshCounter}` || `user-${Math.random()}`}
                        title={el.user.name || 'İsimsiz Kullanıcı'}
                        placement="top"
                        arrow
                    >
                        <MDAvatar
                            src={el.user.profilePicture || team4}
                            alt={el.user.name || 'İsimsiz Kullanıcı'}
                            size={avatarSize}
                            sx={{
                                ...avatarStyles,
                                ml: index === 0 ? 0 : -0.5,
                                mr: 0.25,
                                zIndex: uniqueResponsibles.length - index,
                            }}
                            bgColor="info"
                        />
                    </Tooltip>
                ))}
            {showAddButton && productionID && (
                <AddResponsible
                    allResponsibles={uniqueResponsibles.map((el) => el.user).filter(Boolean)}
                    productionID={productionID}
                    aaroOrder={aaroOrder}
                    users={allUsers || []}
                    onUpdateSuccess={handleUpdateSuccess}
                    size={avatarSize}
                />
            )}
        </MDBox>
    );
};

// PropTypes for better documentation and validation
ProductionResponsibles.propTypes = {
    showAddButton: PropTypes.bool,
    avatarSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
    additionalStyle: PropTypes.object,
    production: PropTypes.object,
};

export default ProductionResponsibles;
